import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { getFieldPractices, getFieldPracticesAdmin, getFieldPracticesSimulations } from "services";

import { getEnvironment } from "utils";

import { GET_FIELD_PRACTICES } from "consts";

import { FieldPracticeResponse } from "models";

type QueryFnParams = {
  fieldId: string;
  simulationId?: string | null;
  isAdmin?: boolean;
};

const env = getEnvironment();

export const useGetFieldPractices = ({
  fieldId,
  inView,
  simulationId,
  isAdmin = env.admin,
}: QueryFnParams & {
  inView: boolean;
}) => {
  // Not having { useSuspense: false } caused unintended UI behavior — like a drawer opening and closing on mount,
  // due to remounting triggered by Suspense fallback.
  // DO NOT REMOVE unless you're explicitly handling Suspense with fallback UIs.
  const { t } = useTranslation(["practices"], { useSuspense: false });

  return useQuery<FieldPracticeResponse>({
    queryKey: [GET_FIELD_PRACTICES, fieldId, simulationId, isAdmin],
    queryFn: async () => {
      const fieldPracticeData = await queryData({ fieldId, simulationId, isAdmin });

      // Why is it `cropName` here and not `crop.name` like everywehre else :D
      const cropsWithInjectedNames = fieldPracticeData.crops.map((crop) => ({ ...crop, cropName: t(`crops.${crop.translationSlug}`) }));

      return {
        ...fieldPracticeData,
        crops: cropsWithInjectedNames,
      };
    },
    enabled: inView && !!fieldId,
    staleTime: 1000 * 60 * 60 * 24, // 1 day
  });
};

const queryData = ({ fieldId, simulationId, isAdmin }: QueryFnParams) => {
  if (isAdmin) {
    return getFieldPracticesAdmin({ fieldId });
  }

  if (simulationId) {
    return getFieldPracticesSimulations({ fieldId, simulationId });
  }

  return getFieldPractices({ fieldId });
};
